import { Link } from "react-router-dom";
import PageNavbar from "@/components/feature/PageNavbar";
import PageFooter from "@/components/feature/PageFooter";

const STATS = [
  { value: "50+", label: "Utility apps launched since 2020" },
  { value: "12M+", label: "Total app installs worldwide" },
  { value: "30+", label: "Countries with active users" },
  { value: "40+", label: "Specialists — developers, marketers, designers & QA" },
];

const EXPERTISE = [
  { icon: "ri-smartphone-line", title: "Mobile Utilities", desc: "Cleaner and optimizer apps for iOS and Android that solve real user problems." },
  { icon: "ri-computer-line", title: "Desktop Apps", desc: "Powerful utilities for Windows and macOS to boost productivity and performance." },
  { icon: "ri-brain-line", title: "AI Services", desc: "Smart tools powered by machine learning for automation and intelligent insights." },
  { icon: "ri-line-chart-line", title: "ASO Promotion", desc: "App Store Optimization that pushes your product to the top of search results." },
  { icon: "ri-advertisement-line", title: "Media Buying", desc: "Strategic ad campaigns across platforms to acquire high-quality users at scale." },
  { icon: "ri-customer-service-line", title: "IT Consulting", desc: "Expert guidance on technology decisions, architecture, and development roadmaps." },
  { icon: "ri-palette-line", title: "UI/UX Design", desc: "User-centric interfaces crafted for clarity, speed, and conversion." },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
      <PageNavbar />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ padding: "80px 16px 50px" }}>
        <div className="mx-auto" style={{ maxWidth: 1440 }}>
          <div style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 16 }}>
            Who we are
          </div>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 72px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#ECECEC", margin: 0, maxWidth: 900 }}>
            We turn great ideas into apps people actually use — then we make sure those apps get found.
          </h1>
          <p style={{ fontSize: "clamp(15px, 1.2vw, 20px)", color: "#8A8A8A", lineHeight: 1.6, marginTop: 20, maxWidth: 720 }}>
            Since 2020, Intellivex has been the team behind dozens of utilities you might already have on your phone. We don't just write code — we build, polish, launch, and grow every product until it earns its place on the home screen.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto" style={{ maxWidth: 1440, padding: "0 16px" }}>
        <div className="flex flex-col lg:grid gap-10 lg:gap-16" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 40 }}>
          <div>
            <h2 style={{ fontSize: "clamp(24px, 2vw, 32px)", fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 20 }}>
              How we got started
            </h2>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7 }}>
              Intellivex was born out of a simple observation: too many solid utility apps were getting lost in crowded app stores, and too many great ideas never saw the light of day because creators struggled with the marketing side. We decided to close that gap under one roof.
            </p>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginTop: 16 }}>
              Today, we're a tight-knit crew of developers, marketers, and designers who speak both engineering and growth fluently. Our playbook covers everything from early prototyping to ASO, paid user acquisition, and beyond — and we run it relentlessly until the numbers move.
            </p>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginTop: 16 }}>
              Whether it's a battery-saving AI tool or a desktop optimizer, our approach stays the same: research first, build with precision, ship fast, and then scale with data — not guesswork.
            </p>
          </div>
          <div>
            <h2 style={{ fontSize: "clamp(24px, 2vw, 32px)", fontWeight: 600, color: "#ECECEC", letterSpacing: "-0.02em", marginBottom: 20 }}>
              The team behind the tools
            </h2>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7 }}>
              Every project at Intellivex has a dedicated squad: a project manager who keeps the momentum, engineers who obsess over performance, designers who hate clutter, marketers who live in the dashboards, and QA engineers who break things so your users never will.
            </p>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginTop: 16 }}>
              Beyond building our own products, we advise other teams on tech stack decisions, process architecture, and go-to-market strategy. Our consulting arm has helped startups and established companies alike avoid costly missteps and ship faster.
            </p>
            <p style={{ fontSize: 16, color: "#8A8A8A", lineHeight: 1.7, marginTop: 16 }}>
              And then there's the growth engine — the part we're genuinely obsessed with. We craft acquisition strategies, run media campaigns, optimize store listings, and keep tweaking until every install costs what it should. For us, the launch is just the beginning.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" style={{ marginTop: 60, marginBottom: 60 }}>
          {STATS.map((s) => (
            <div key={s.value} className="group relative" style={{ borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 18 }}>
              <div style={{ fontSize: 44, fontWeight: 500, color: "#ECECEC", letterSpacing: "-0.02em", lineHeight: 1 }} className="group-hover:!text-[#3B82F6] transition-colors duration-300">
                {s.value}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: "#8A8A8A" }}>
                {s.label}
              </div>
              <div aria-hidden style={{ position: "absolute", top: -1, left: 0, height: 1, width: "0%", background: "#3B82F6", transition: "width 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)" }} className="group-hover:!w-full" />
            </div>
          ))}
        </div>

        {/* Expertise */}
        <div style={{ marginBottom: 80 }}>
          <div style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 30 }}>
            Our Expertise
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {EXPERTISE.map((item) => (
              <div key={item.title} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 28, background: "rgba(255,255,255,0.02)" }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(147,197,253,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                  <i className={item.icon} style={{ fontSize: 20, color: "#93C5FD" }} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: "#ECECEC", marginBottom: 8 }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: "#8A8A8A", lineHeight: 1.5 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  );
}